/**
 * The office lodge form's layout and its form state → `buildLodge` values (13-24), as pure functions.
 *
 * `LodgeForm.tsx` renders what this module says, so the reveal-and-clear rules are tested here without a DOM:
 *   - **Sections** per register, in the plan's grouping, covering every FIELDSET §3.2 column exactly once except
 *     `Attachments` (a line, not a control, D-13-L-6) and the identity column (shown read-only, D-13-L-8).
 *   - **Visibility** comes from the field set's own `lodge` rules and the builder's word-rules (`lodgeConditions`),
 *     never retyped: a conditional column shows only while its condition holds; the client picker only on a client
 *     stream; the anonymous contact columns hide when `Anonymous` = Yes (§1.6); `WhistleblowerElection` sits behind
 *     the "This is a whistleblower disclosure" tick. A hidden column is cleared (`clearHidden`) and never sent
 *     (`toLodgeValues`).
 *   - **Dates** (D-13-L-10): a day is the date input's `YYYY-MM-DD` text, used as is (no `Date`); a time is `HH:mm`.
 *     A timed day becomes `{ day, time }` for the builder, which converts it by the Melbourne rules (`fieldValues`).
 *     `ReportedOn` stays the builder's submit-instant pre-fill until the person chooses to change it.
 *   - **One form check the builder does not make:** `InjuredWorkerName` holds a name, never an email address
 *     (`name-not-email`, 13.2: D-13.2-A-3, D-13.2-P-2; it replaced D-13-24-5's Harmony-address check on the UPN).
 * No React, no Graph, no `Date.now()`.
 */

import { CLIENT_STREAMS, fieldOf, lodgeColumns } from '../config/fieldset/registerFields';
import type { FieldSpec } from '../config/fieldset/registerFields';
import type { RecordRegister, Refusal, RequiredRule } from '../types/records';
import { checkUpn } from './fieldValues';
import { lodgeConditions } from './lodgeBuilder';
import type { LodgeOptions, LodgeValues, WallTime } from './lodgeBuilder';

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

export interface LodgeSection {
  id: string;
  title: string;
  /** Lodge columns in display order. `RecipientName` + `RecipientClientId` render as one client picker. */
  columns: readonly string[];
  /** Shown collapsed until opened (or until a refusal sits inside it). */
  collapsed?: boolean;
}

/** The columns the client picker fills (D-13-L-7). */
export const PICKER_COLUMNS: readonly string[] = ['RecipientName', 'RecipientClientId'];
/** Behind the "This is a whistleblower disclosure" tick. */
export const WHISTLEBLOWER_COLUMN = 'WhistleblowerElection';
/** Pre-filled with the submit instant until changed (FIELDSET §3.2.1 row 9). */
export const REPORTED_ON = 'ReportedOn';
/** Not a control (D-13-L-6). */
export const ATTACHMENTS = 'Attachments';
/** The injured worker, by name (13.2, D-13.2-P-2): the office lodge's field. */
export const INJURED_WORKER_NAME = 'InjuredWorkerName';
/**
 * The injured worker's UPN. Since 13.2 (D-13.2-P-2) FieldLink's self-report is its only writer and it is not on the
 * office lodge form; kept for the record page, which may only clear it (13.2-03 / 13.2-09).
 */
export const INJURED_WORKER_UPN = 'InjuredWorkerUPN';

const SECTIONS: Readonly<Record<RecordRegister, readonly LodgeSection[]>> = {
  incidents: [
    {
      id: 'what',
      title: 'What happened',
      columns: ['Title', 'BusinessUnit', 'Category', 'OccurredOn', 'Location', 'Description', 'ImmediateActions'],
    },
    {
      id: 'who',
      title: 'Who was affected',
      columns: [
        'IncidentBasis', 'HarmKind', 'HarmDescription', 'RecipientName', 'RecipientClientId', 'WorkerInjured',
        'InjuredWorkerName', 'InjuredWorkerOccupation',
      ],
    },
    {
      id: 'more',
      title: 'More about this report (optional)',
      collapsed: true,
      columns: [
        'Witnesses', 'OtherPersonsInvolved', 'PersonsContactDetails', 'WitnessContactDetails', 'ReportedOnBehalfOf',
        'ReporterPhone', 'ReportedToRole', 'ReportedToAt', 'ReportedOn',
      ],
    },
  ],
  complaints: [
    {
      id: 'about',
      title: 'The complaint',
      columns: ['Title', 'Category', 'BusinessUnit', 'Description', 'ReceivedOn', 'Channel', 'WhistleblowerElection'],
    },
    {
      id: 'raised',
      title: 'Who raised it',
      columns: ['Anonymous', 'SourceType', 'OnBehalfOf', 'ComplainantName', 'ComplainantPhone', 'ComplainantEmail'],
    },
    { id: 'client', title: 'Client affected', columns: ['RecipientName', 'RecipientClientId'] },
  ],
  feedback: [
    {
      id: 'about',
      title: 'The feedback',
      columns: ['Title', 'Category', 'BusinessUnit', 'Description', 'ReceivedOn', 'Channel', 'WhistleblowerElection'],
    },
    {
      id: 'raised',
      title: 'Who gave it',
      columns: ['Anonymous', 'SourceType', 'ProviderName', 'ProviderPhone', 'ProviderEmail'],
    },
  ],
  risk: [{ id: 'risk', title: 'The risk', columns: ['Title', 'RiskCategory', 'BusinessUnit', 'Description'] }],
  ci: [
    {
      id: 'ci',
      title: 'The improvement',
      columns: ['Title', 'Description', 'Issue', 'Category', 'BusinessUnit', 'IdentifiedOn'],
    },
  ],
};

export function lodgeSections(register: RecordRegister): readonly LodgeSection[] {
  return SECTIONS[register];
}

/** Every column the form renders a control for, in section order. */
export function formColumns(register: RecordRegister): readonly string[] {
  return SECTIONS[register].flatMap((s) => s.columns);
}

/** The section a column sits in. */
export function sectionOf(register: RecordRegister, column: string): LodgeSection | undefined {
  return SECTIONS[register].find((s) => s.columns.includes(column));
}

/** The field spec of a lodge column (throws on a column the field set does not name: a layout bug). */
export function specOf(register: RecordRegister, column: string): FieldSpec {
  const spec = fieldOf(register, column);
  if (!spec) throw new Error(`lodgeLayout: ${register}.${column} is not in the field set`);
  return spec;
}

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

/** What the person has entered. Day columns hold the date input's text; their time sits under `timeKey(column)`. */
export interface LodgeFormState {
  values: Readonly<Record<string, string>>;
  /** "This is a whistleblower disclosure" is ticked. */
  whistleblower: boolean;
  /** The person chose to change `ReportedOn` from "when I press Lodge". */
  reportedOnEdited: boolean;
  /** "Not on the client lists" is ticked (D-13-L-7). */
  clientUnlisted: boolean;
}

export const EMPTY_FORM: LodgeFormState = {
  values: {},
  whistleblower: false,
  reportedOnEdited: false,
  clientUnlisted: false,
};

/** Where a day column's time part is kept in `values`. */
export function timeKey(column: string): string {
  return `${column}@time`;
}

const blank = (v: string | undefined) => v === undefined || v.trim() === '';

/** Does a `RequiredRule` hold on the raw form values? (`always` → true, `optional` / none → false.) */
export function ruleHolds(rule: RequiredRule | undefined, values: Readonly<Record<string, string>>): boolean {
  if (rule === 'always') return true;
  if (rule === undefined || rule === 'optional') return false;
  const v = (values[rule.when] ?? '').trim();
  return 'in' in rule ? rule.in.includes(v) : !rule.notIn.includes(v);
}

/** Is this column on screen now? */
export function isVisible(register: RecordRegister, column: string, state: LodgeFormState): boolean {
  const cond = lodgeConditions(register);
  if (cond.onlyWhen.includes(column)) return ruleHolds(specOf(register, column).required.lodge, state.values);
  if (PICKER_COLUMNS.includes(column)) return CLIENT_STREAMS.includes(state.values.BusinessUnit ?? '');
  if (cond.anonymousContact.includes(column)) return state.values.Anonymous !== cond.anonymousYes;
  if (column === WHISTLEBLOWER_COLUMN) return state.whistleblower;
  return true;
}

/** Is this column marked required now? (`ReportedOn` is required but pre-filled, so it is never empty.) */
export function isRequiredNow(register: RecordRegister, column: string, state: LodgeFormState): boolean {
  if (column === 'RecipientClientId' && state.clientUnlisted) return false;
  return ruleHolds(specOf(register, column).required.lodge, state.values);
}

/** The state with every hidden column's value (and time part) removed. Run after each change. */
export function clearHidden(register: RecordRegister, state: LodgeFormState): LodgeFormState {
  let values: Record<string, string> | null = null;
  // Repeat until stable: clearing one column can hide another (a cleared `HarmKind` hides `HarmDescription`).
  for (let pass = 0; pass < 4; pass += 1) {
    let changed = false;
    const current = { ...state, values: values ?? state.values };
    for (const column of formColumns(register)) {
      if (isVisible(register, column, current)) continue;
      const v = (values ?? state.values)[column];
      const t = (values ?? state.values)[timeKey(column)];
      if (v !== undefined || t !== undefined) {
        values = { ...(values ?? state.values) };
        delete values[column];
        delete values[timeKey(column)];
        changed = true;
      }
    }
    if (!changed) break;
  }
  const next = values ? { ...state, values } : state;
  // An unticked "not on the lists" keeps a picked client; a tick drops the picked id (the name stays typed).
  if (next.clientUnlisted && next.values.RecipientClientId !== undefined) {
    const v = { ...next.values };
    delete v.RecipientClientId;
    return { ...next, values: v };
  }
  return next;
}

// ---------------------------------------------------------------------------
// → buildLodge
// ---------------------------------------------------------------------------

/** The form's values for `buildLodge`, keyed by internal name. Hidden columns are never included. */
export function toLodgeValues(register: RecordRegister, state: LodgeFormState): LodgeValues {
  const out: Record<string, string | WallTime> = {};
  for (const column of formColumns(register)) {
    if (!isVisible(register, column, state)) continue;
    if (column === 'RecipientClientId' && state.clientUnlisted) continue;
    const spec = specOf(register, column);
    const value = state.values[column];
    if (spec.kind === 'day-existing' || spec.kind === 'instant') {
      if (column === REPORTED_ON && !state.reportedOnEdited) continue; // the builder's pre-fill (opts)
      const time = state.values[timeKey(column)];
      if (blank(value) && blank(time)) continue;
      if (spec.kind === 'instant' || (spec.timeWhenKnown && !blank(time))) {
        out[column] = { day: (value ?? '').trim(), time: (time ?? '').trim() };
      } else {
        out[column] = (value ?? '').trim();
      }
      continue;
    }
    if (value !== undefined) out[column] = value;
  }
  return out;
}

/** The form flags for `buildLodge`'s `opts` (the caller adds `auditId`). */
export function lodgeFlags(register: RecordRegister, state: LodgeFormState): Omit<LodgeOptions, 'auditId'> {
  const flags: Omit<LodgeOptions, 'auditId'> = {};
  if (state.clientUnlisted && isVisible(register, 'RecipientName', state)) flags.clientUnlisted = true;
  if (lodgeColumns(register).includes(REPORTED_ON) && !state.reportedOnEdited) flags.reportedOnDefaulted = true;
  return flags;
}

/** The domain of a UPN, lower-cased, or `null`. */
export function domainOf(upn: string | null | undefined): string | null {
  const r = checkUpn(upn ?? '');
  return r.ok ? r.value.slice(r.value.lastIndexOf('@') + 1) : null;
}

/**
 * The form's own refusals (13.2, D-13.2-A-3 / D-13.2-P-2): the injured worker is named, so a value holding an `@`
 * (an email address) is refused `name-not-email`. Only while the field is on screen. The lodger's UPN is no longer
 * needed: D-13-24-5's Harmony-address check went with the UPN field.
 */
export function formRefusals(register: RecordRegister, state: LodgeFormState): Refusal[] {
  const out: Refusal[] = [];
  if (lodgeColumns(register).includes(INJURED_WORKER_NAME) && isVisible(register, INJURED_WORKER_NAME, state)) {
    const value = state.values[INJURED_WORKER_NAME] ?? '';
    if (!blank(value) && value.includes('@')) {
      out.push({
        code: 'name-not-email',
        column: INJURED_WORKER_NAME,
        message: `${specOf(register, INJURED_WORKER_NAME).label}: use their name, not an email address.`,
      });
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Melbourne wall clock (for the ReportedOn "change" inputs; D-13-L-10: Intl, never a fixed offset)
// ---------------------------------------------------------------------------

const MELBOURNE_HM = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'Australia/Melbourne',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** `now` as a Melbourne `HH:mm`. */
export function melbourneTimeOf(now: Date): string {
  const parts = MELBOURNE_HM.formatToParts(now);
  const get = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? '00';
  return `${String(Number(get('hour')) % 24).padStart(2, '0')}:${get('minute')}`;
}
